import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useListState from '../hooks/useListState';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
  InputAdornment,
  TableSortLabel
} from '@mui/material';
import { 
  Search as SearchIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Stop as StopIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Clear as ClearIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import processCodexService from '../services/processCodexService';
import processService from '../services/processService';
import { STATUS_CONFIG, getStatusConfig } from '../config/statusConfig';

const ProcessList = () => {
  const navigate = useNavigate();
  
  // State for data
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const sentinelRef = useRef(null);
  const sentinelVisibleRef = useRef(false);

  // URL-synchronized state for filters, pagination, and sorting
  const {
    searchQuery,
    statusFilter,
    page,
    rowsPerPage,
    orderBy,
    order,
    setPage,
    handleSearchChange,
    handleStatusFilterChange,
    handleSortChange,
    clearSearch: originalClearSearch,
    buildFilters
  } = useListState({
    defaults: {
      search: '',
      status: '',
      page: 0,
      size: 25,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    },
    options: {
      searchDebounceMs: 500
    }
  });

  // States for modals
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelResponse, setCancelResponse] = useState(null);

  // Custom clearSearch function that also resets the isSearching state
  const clearSearch = () => {
    setIsSearching(false);
    setHasMore(true);
    setIsFetchingMore(false);
    originalClearSearch();
    setPage(0);
  };
  
  // Reset pagination and trigger data reload
  const refetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasMore(true);
    setIsFetchingMore(false);
    setPage(0);
    // Force a re-render by incrementing the refresh trigger
    setRefreshTrigger(prev => prev + 1);
  }, [setPage]);

  // Enhanced search change handler with loading state
  const handleSearchChangeWithLoading = useCallback((event) => {
    if (!event || !event.target) return;
    
    setIsSearching(true);
    handleSearchChange(event);
    setHasMore(true);
    setIsFetchingMore(false);
    setPage(0);
    
    // Reset the searching state after a delay
    setTimeout(() => {
      setIsSearching(false);
    }, 600); // Slightly longer than the debounce
  }, [handleSearchChange, setPage]);

  const handleStatusChange = useCallback((event) => {
    handleStatusFilterChange(event);
    setHasMore(true);
    setIsFetchingMore(false);
    setPage(0);
  }, [handleStatusFilterChange, setPage]);

  const handleLoadMore = useCallback(() => {
    if (loading || isFetchingMore || !hasMore) {
      return;
    }
    setIsFetchingMore(true);
    setPage(page + 1);
  }, [loading, isFetchingMore, hasMore, page, setPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        sentinelVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore]);

  useEffect(() => {
    if (!loading && !isFetchingMore && hasMore && sentinelVisibleRef.current) {
      handleLoadMore();
    }
  }, [loading, isFetchingMore, hasMore, handleLoadMore]);

  // Load data when filters or pagination change
  useEffect(() => {
    let isMounted = true;
    const isFirstPage = page === 0;

    const loadData = async () => {
      if (isFirstPage) {
        setLoading(true);
        setHasMore(true);
      }

      try {
        setError(null);
        const filters = buildFilters();
        const response = await processCodexService.getAll(filters);

        if (!isMounted) {
          return;
        }

        const content = response.content || [];
        const lastFlag = typeof response.last === 'boolean'
          ? response.last
          : (
              (typeof response.number === 'number' && typeof response.totalPages === 'number')
                ? response.number + 1 >= response.totalPages
                : content.length < rowsPerPage
            );

        setHasMore(!lastFlag);

        if (isFirstPage) {
          setProcesses(content);
        } else {
          setProcesses((prev) => {
            if (!prev || prev.length === 0) {
              return content;
            }
            const existingIds = new Set(prev.map((item) => item.id));
            const merged = content.filter((item) => !existingIds.has(item.id));
            return [...prev, ...merged];
          });
        }
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }
        console.error('Error fetching processes:', fetchError);
        setError('Failed to load processes');
        if (isFirstPage) {
          setProcesses([]);
        }
      } finally {
        if (!isMounted) {
          return;
        }
        /* I don't know what this was for, mducoli, 2025-10-14
        if (isFirstPage) {
          setLoading(false);
        }
        */
        setLoading(false);
        setIsFetchingMore(false);
        setIsSearching(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [page, rowsPerPage, orderBy, order, statusFilter, searchQuery, buildFilters, refreshTrigger]);

  // Handle sort click (using the hook's handler)
  const handleRequestSort = handleSortChange;

  const handleRowClick = (process) => {
    //TODO: handle this with the config
    if (process.resultMessage || process.status === STATUS_CONFIG.PROCESS.FAILED.code || process.status === STATUS_CONFIG.PROCESS.COMPLETED.code || process.status === STATUS_CONFIG.PROCESS.CANCELLED.code) {
      setSelectedError(process.resultMessage || 'Nessun messaggio disponibile');
      setSelectedStatus(process.status);
      setErrorModalOpen(true);
    }
    if (process.status === STATUS_CONFIG.PROCESS.TO_VALIDATE.code) {
      setSelectedError('Necessaria validazione manuale');
      setSelectedStatus(process.status);
      setErrorModalOpen(true);
    }
  };

  const handleCodexClick = (codexId) => {
    navigate(`/codex/${codexId}`);
  };

  const handleCancelClick = async (processId, e) => {
    e.stopPropagation(); // Prevent row click event
    try {
      const response = await processCodexService.cancelProcess(processId);
      if (response.data === true) {
        setCancelResponse({ success: true });
      } else {
        setCancelResponse({ 
          success: false, 
          isWarning: true,
          message: 'Il processo non risulta essere in esecuzione.'
        });
      }
      setCancelModalOpen(true);
      // Always refetch the processes list
      await refetchData();
    } catch (error) {
      setCancelResponse({ 
        success: false, 
        isWarning: false,
        message: error.message || 'Si è verificato un errore durante l\'annullamento del processo.' 
      });
      setCancelModalOpen(true);
    }
  };

  const handleRetryProcess = async (codexId, processId, e) => {
    e.stopPropagation(); // Prevent row click event
    try {
      setError(null);
      await processService.restartCodexProcess(codexId, processId, {}); 
      // Refresh the processes list
      await refetchData();
    } catch (err) {
      console.error('Error retrying process:', err);
      setError('Failed to retry process. Please try again.');
    }
  };

  // Format date
  const formatDateOnly = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
  };

  const formatTimeOnly = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) {
      return '—';
    }
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return '—';
    }
    const totalSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => value.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  if (loading && !isSearching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cronologia</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per processId o messaggio di stato..."
                value={searchQuery}
                onChange={handleSearchChangeWithLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching ? (
                        <CircularProgress size={20} />
                      ) : searchQuery ? (
                        <IconButton size="small" onClick={clearSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Stato"
                    onChange={handleStatusChange}
                    renderValue={(selected) => {
                      if (!selected) return "Tutti";
                      const statusConfig = getStatusConfig('PROCESS', selected);
                      return (
                        <Chip 
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                        />
                      );
                    }}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {Object.entries(STATUS_CONFIG.PROCESS).map(([status, config]) => (
                      <MenuItem key={status} value={status}>
                        <Chip 
                          label={config.label}
                          color={config.color}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell sx={{ width: 120, minWidth: 110 }}>
                  <TableSortLabel
                    active={orderBy === 'startedAt'}
                    direction={orderBy === 'startedAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('startedAt')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 120, minWidth: 110 }}>
                  <TableSortLabel
                    active={orderBy === 'startedAt'}
                    direction={orderBy === 'startedAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('startedAt')}
                  >
                    Ora inizio
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 120, minWidth: 110 }}>
                  <TableSortLabel
                    active={orderBy === 'completedAt'}
                    direction={orderBy === 'completedAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('completedAt')}
                  >
                    Ora fine
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 110, minWidth: 100 }}>
                  Durata
                </TableCell>
                <TableCell sx={{ width: 180, minWidth: 160 }}>
                  Tipo
                </TableCell>
                <TableCell sx={{ width: 220, minWidth: 200 }}>
                  <TableSortLabel
                    active={orderBy === 'codex.Code'}
                    direction={orderBy === 'codex.Code' ? order : 'asc'}
                    onClick={() => handleRequestSort('codex.Code')}
                  >
                    Documento
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 130, minWidth: 120 }}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 120, minWidth: 110 }} align="center">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processes.length > 0 ? (
                processes.map((process) => {
                  const statusConfig = getStatusConfig('PROCESS', process.status);
                  return (
                    <TableRow 
                      key={process.id}
                      hover
                      sx={{ 
                        cursor: process.resultMessage ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: process.resultMessage ? 'rgba(0, 0, 0, 0.04)' : undefined
                        }
                      }}
                      onClick={() => handleRowClick(process)}
                    >
                      <TableCell sx={{ width: 120 }}>{formatDateOnly(process.startedAt || process.createdAt)}</TableCell>
                      <TableCell sx={{ width: 120 }}>{formatTimeOnly(process.startedAt)}</TableCell>
                      <TableCell sx={{ width: 120 }}>{formatTimeOnly(process.completedAt)}</TableCell>
                      <TableCell sx={{ width: 110 }}>{formatDuration(process.startedAt, process.completedAt)}</TableCell>
                      <TableCell sx={{ width: 180, maxWidth: 180, wordBreak: 'break-word' }}>
                        {process.codexTypeDescription || process.codexTypeCode || '-'}
                      </TableCell>
                      <TableCell sx={{ width: 220, maxWidth: 220, wordBreak: 'break-word' }}>
                        {process.codexCode || 'undefined'}
                      </TableCell>
                      <TableCell sx={{ width: 130 }}>
                        <Chip 
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small" 
                        />
                      </TableCell>
                      <TableCell sx={{ width: 120 }} align="center">
                        <Box sx={{ display: 'inline-flex', gap: 1 }}>
                          <Tooltip title="Vai al codex">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCodexClick(process.codexId);
                              }}
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                          {( process.status === STATUS_CONFIG.PROCESS.RUNNING.code || process.status === STATUS_CONFIG.PROCESS.TO_VALIDATE.code ) && (
                            <Tooltip title="Annulla processo">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleCancelClick(process.id, e)}
                              >
                                <StopIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          { (process.status === STATUS_CONFIG.PROCESS.FAILED.code || process.status === STATUS_CONFIG.PROCESS.REJECTED.code) && (
                            <Tooltip title="Riprova Processo">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => handleRetryProcess(process.codexId, process.processId, e)}
                              >
                                <ReplayIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nessun processo trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box ref={sentinelRef} sx={{ height: hasMore ? 24 : 0 }} />
        {isFetchingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!hasMore && processes.length > 0 && (
          <Box sx={{ textAlign: 'center', py: 1, color: 'text.secondary' }}>
            Nessun altro processo da mostrare
          </Box>
        )}
      </Paper>

      {/* Result Message Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: getStatusConfig('PROCESS', selectedStatus).color === 'error' ? 'error.main' :
                  getStatusConfig('PROCESS', selectedStatus).color === 'success' ? 'success.main' :
                  getStatusConfig('PROCESS', selectedStatus).color === 'info' ? 'info.main' :
                  getStatusConfig('PROCESS', selectedStatus).color === 'warning' ? 'warning.main' :
                  getStatusConfig('PROCESS', selectedStatus).color === 'default' ? 'grey.700' : 'grey.500',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {selectedStatus === STATUS_CONFIG.PROCESS.FAILED.code ? <ErrorIcon /> : 
           selectedStatus === STATUS_CONFIG.PROCESS.COMPLETED.code ? <CheckCircleIcon /> :
           selectedStatus === STATUS_CONFIG.PROCESS.RUNNING.code ? <CircularProgress size={24} color="inherit" /> :
           selectedStatus === STATUS_CONFIG.PROCESS.TO_VALIDATE.code ? <CircularProgress size={24} color="inherit" /> :
           selectedStatus === STATUS_CONFIG.PROCESS.CANCELLED.code ? <CancelIcon /> :
           <SearchIcon />}
          {getStatusConfig('PROCESS', selectedStatus).label}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" component="pre" sx={{ 
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            bgcolor: 'grey.50',
            p: 2,
            borderRadius: 1
          }}>
            {selectedError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Response Modal */}
      <Dialog
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: cancelResponse?.isWarning ? 'warning.main' :
                  cancelResponse?.success ? 'success.main' : 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {cancelResponse?.isWarning ? <WarningIcon /> :
           cancelResponse?.success ? <CheckCircleIcon /> : <ErrorIcon />}
          {cancelResponse?.isWarning ? 'Attenzione' :
           cancelResponse?.success ? 'Processo Annullato' : 'Errore'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {cancelResponse?.success 
              ? 'Il processo è stato annullato con successo.'
              : cancelResponse?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelModalOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessList; 
